import React, { useState, useEffect, useMemo } from 'react';
import { fetchTopQuestions } from '../api';
import Loader from '../components/Loader';

export default function TopQuestionsPage({ setView, setSelectedQuestion }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('All');

  useEffect(() => {
    fetchTopQuestions(50)
      .then(data => setQuestions(data.questions || []))
      .catch(() => setError('Failed to load questions. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  // Collect all unique companies across questions
  const allCompanies = useMemo(() => {
    const set = new Set();
    questions.forEach(q => (q.companies || []).forEach(c => set.add(c)));
    return ['All', ...Array.from(set).sort()];
  }, [questions]);

  // Filter questions by selected company
  const filtered = useMemo(() => {
    if (selectedCompany === 'All') return questions;
    return questions.filter(q => (q.companies || []).includes(selectedCompany));
  }, [questions, selectedCompany]);

  const handleClick = (q) => {
    setSelectedQuestion(q);
    setView('question-reviews');
  };

  return (
    <div style={{ animation: 'fadeUp 0.35s ease-out' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 900, margin: '0 0 4px', color: 'var(--text)' }}>
          Top Interview Questions
        </h1>
        <p className="mobile-hide" style={{ fontSize: 13, color: 'var(--text3)', margin: 0 }}>
          Real questions extracted and clustered from interview experiences shared by the community
        </p>
      </div>

      {loading ? (
        <Loader inline message="Analysing interview questions…" />
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--red)', fontSize: 14 }}>
          {error}
        </div>
      ) : questions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)', fontSize: 14 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>No interview questions yet</div>
          <div style={{ fontSize: 13 }}>Questions will appear here once users start sharing kahanis with interview questions.</div>
        </div>
      ) : (
        <>
          {/* Company filter chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {allCompanies.map(c => {
              const active = selectedCompany === c;
              return (
                <button
                  key={c}
                  onClick={() => setSelectedCompany(c)}
                  style={{
                    background: active ? '#7c3aed' : 'var(--surface)',
                    color: active ? '#fff' : 'var(--text2)',
                    border: `1px solid ${active ? '#7c3aed' : 'var(--border)'}`,
                    borderRadius: 20, padding: '5px 12px',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'var(--sans)', transition: 'all 0.15s',
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14, fontWeight: 500 }}>
            {filtered.length} unique question{filtered.length !== 1 ? 's' : ''}
            {selectedCompany !== 'All' ? ` at ${selectedCompany}` : ''} · click any to see matching kahanis
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map((q, i) => (
              <QuestionRow key={q.id || i} question={q} rank={i + 1} onClick={() => handleClick(q)} />
            ))}
          </div>
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)', fontSize: 14 }}>
              No questions found for {selectedCompany}.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function QuestionRow({ question: q, rank, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(124,58,237,0.03)' : 'var(--surface)',
        border: `1px solid ${hovered ? '#7c3aed' : 'var(--border)'}`,
        borderRadius: 10, padding: '14px 16px',
        cursor: 'pointer', transition: 'all 0.15s',
        display: 'flex', gap: 14, alignItems: 'flex-start',
        boxShadow: hovered ? '0 4px 12px rgba(124,58,237,0.08)' : '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      {/* Rank */}
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: rank <= 3
          ? (rank === 1 ? '#f59e0b' : rank === 2 ? '#94a3b8' : '#b45309')
          : 'var(--surface2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 800, color: rank <= 3 ? '#fff' : 'var(--text3)',
        fontFamily: 'var(--mono)',
      }}>
        {rank}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 600, lineHeight: 1.5,
          color: hovered ? '#7c3aed' : 'var(--text)',
          transition: 'color 0.15s',
        }}>
          {q.question}
        </div>

        {/* Meta */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center' }}>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
            background: 'rgba(124,58,237,0.1)', color: '#7c3aed',
          }}>
            {q.count} {q.count === 1 ? 'kahani' : 'kahanis'}
          </span>
          {q.totalUpvotes > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
              background: 'rgba(249,115,22,0.1)', color: '#f97316',
            }}>
              ▲ {q.totalUpvotes} upvotes
            </span>
          )}
          {(q.companies || []).slice(0, 3).map(c => (
            <span key={c} style={{
              fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20,
              background: 'var(--surface2)', color: 'var(--text3)',
            }}>
              {c}
            </span>
          ))}
          {(q.companies || []).length > 3 && (
            <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500 }}>
              +{q.companies.length - 3} more
            </span>
          )}
        </div>
      </div>

      {/* Arrow */}
      <svg
        width="16" height="16" viewBox="0 0 24 24"
        fill="none" stroke={hovered ? '#7c3aed' : 'var(--text3)'}
        strokeWidth="2.5" style={{ flexShrink: 0, marginTop: 4, transition: 'stroke 0.15s' }}
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </div>
  );
}
